<div *ngIf="isLoading; else content">
  <div class="card bg-white">
    <div class="card-content">
      <div class="mb-3">
        <app-content-shimmer height="60px"></app-content-shimmer>
      </div>
      <div class="mb-3">
        <app-content-shimmer height="60px"></app-content-shimmer>
      </div>
    </div>
  </div>
</div>

<ng-template #content>
  <div class="card h-full bg-white relative"
       [ngClass]="{'border-green border-2': isActive, 'border-grayTableBorder': !isActive}">

    <!-- Active Badge -->
    <div class="bg-primary text-white text-center font-bold" *ngIf="isActive" i18n>AKTIVEN</div>

    <!-- Preferred Badge -->
    <div class="bg-primary text-white text-center text-xs font-bold p-1 absolute rounded-full"
         style="top: -10px; right: -10px;" *ngIf="modulePackage.isPreferred && !isActive" i18n>PRIPOROČENO
    </div>

    <div class="card-content flex flex-row">
      <div class="w-full h-full flex flex-col items-start">

        <!-- Module Name and Price -->
        <div class="flex w-full text-lg">
          <div class="font-bold">
            <span class="uppercase">{{ modulePackage.name }}</span>
          </div>
          <div class="grow flex justify-start md:justify-end items-end">
            <div class="font-bold text-primary text-right">
              {{ getFormattedPrice() }}
              <span *ngIf="!modulePackage.isFree && modulePackage.pricePerUnit" class="text-sm block">
                {{ getBillingUnitLabel() }}
              </span>
              <span *ngIf="isPeriodPrice" class="text-sm block">
                {{ getPeriodPriceLabel() }}
              </span>
              <span *ngIf="!modulePackage.isFree" class="text-xs text-gray block" i18n>
                cena ne vsebuje DDV
              </span>
            </div>
          </div>
        </div>

        <!-- Top description blurb.
             - CarPeak modules: render the full BE description with the
               "Več na CarPeak" phrase turned into a link to the locale's
               CarPeak pricing page. Everything before and after the
               marker stays plain text — only the marker is a link.
             - Everything else: BE description text as-is. -->
        <ng-container *ngIf="isCarPeakModule && carPeakDescriptionParts.hasLink; else genericDescription">
          <div class="text-sm text-grayDark mt-2">
            {{ carPeakDescriptionParts.before
            }}<a [href]="carPeakUrl"
                 target="_blank" rel="noopener noreferrer"
                 class="text-primary font-semibold hover:underline"
            >{{ 'Več na CarPeak' }}</a>{{ carPeakDescriptionParts.after }}
          </div>
        </ng-container>
        <ng-template #genericDescription>
          <div class="text-sm text-grayDark mt-2" *ngIf="modulePackage.description">
            {{ modulePackage.description }}
          </div>
        </ng-template>

        <!-- Period-based Pricing Display (monthly / yearly) -->
        <div class="w-full mt-4" *ngIf="hasPeriodPricing && !isActive">
          <div class="text-sm font-bold mb-2" i18n>Naročnina:</div>
          <div class="grid gap-2" [ngClass]="{'grid-cols-2': modulePackage.priceMonthly != null && modulePackage.priceYearly != null, 'grid-cols-1': !(modulePackage.priceMonthly != null && modulePackage.priceYearly != null)}">
            <div class="rounded border border-grayHover p-2 text-center" *ngIf="modulePackage.priceMonthly != null">
              <div class="text-xs text-gray uppercase" i18n>Mesečno</div>
              <div class="font-bold text-primary">{{ modulePackage.priceMonthly.toFixed(2) }} €<span class="text-xs text-gray font-normal" i18n>/mes</span></div>
            </div>
            <div class="rounded border border-grayHover p-2 text-center relative" *ngIf="modulePackage.priceYearly != null">
              <div class="text-xs text-gray uppercase" i18n>Letno</div>
              <div class="font-bold text-primary">{{ modulePackage.priceYearly.toFixed(2) }} €<span class="text-xs text-gray font-normal" i18n>/leto</span></div>
              <div *ngIf="yearlySavings" class="text-xs text-green font-semibold mt-1">
                <span i18n>Prihraniš</span> {{ yearlySavings.toFixed(2) }} €
              </div>
            </div>
          </div>
        </div>

        <!-- Active period indicator -->
        <div class="flex w-full mt-4" *ngIf="isActive && activePeriod">
          <i class="text-primary fas fa-calendar-alt"></i>
          <div class="text-sm ml-2 text-left font-bold">
            <span i18n>Naročnina:</span>&ngsp;
            <span *ngIf="activePeriod === 'monthly'" i18n>Mesečna</span>
            <span *ngIf="activePeriod === 'yearly'" i18n>Letna</span>
          </div>
        </div>

        <!-- Subscription Pricing Display -->
        <div class="w-full mt-4 bg-grayNavi rounded p-2 text-xs"
             *ngIf="modulePackage.hasSubscriptionOption && !isActive && pricing?.volume_units">
          <span i18n>Dogovorjen paket:</span>
          <strong>{{ pricing!.volume_units }} <span i18n>vozil po</span>
            {{ pricing!.volume_per_vehicle!.toFixed(2) }} &euro;</strong>,
          <span i18n>nato</span> {{ pricing!.per_vehicle.toFixed(2) }} &euro;<span i18n>/vozilo</span>
        </div>

        <!-- Included Units Info -->
        <div class="flex w-full mt-4" *ngIf="modulePackage.includedUnits">
          <i class="text-primary fas fa-gift"></i>
          <!-- Work-order module: show remaining lifetime free units when known. -->
          <div class="text-sm ml-2 text-left font-bold" *ngIf="freeUnitsRemaining !== null" i18n>
            Še {{ freeUnitsRemaining }} od {{ freeUnitsLimit }} brezplačnih enot
          </div>
          <div class="text-sm ml-2 text-left font-bold" *ngIf="freeUnitsRemaining === null" i18n>
            {{ modulePackage.includedUnits }} brezplačnih enot
          </div>
        </div>

        <!-- Usage Dashboard (for per-vehicle billing) -->
        <app-usage-dashboard
          *ngIf="isActive && modulePackage.billingUnit === 'per_vehicle' && activeModulePackage"
          [accountModulePackage]="activeModulePackage">
        </app-usage-dashboard>

        <hr class="h-px w-full mt-4 mb-4 bg-grayHover dark:bg-grayDark">

        <!-- Upgrade Info -->
        <div class="flex w-full mb-4 p-3 bg-blue-50 border border-blue-400 rounded" *ngIf="!isActive && isUpgrade">
          <i class="text-blue-600 fas fa-arrow-circle-up mr-2 mt-1"></i>
          <div class="text-sm text-left">
            <span class="font-bold text-blue-600" i18n>Nadgradnja: </span>
            <span *ngIf="conflictingModule" i18n>
              Z aktivacijo tega modula bo modul "{{ conflictingModule.name }}" samodejno deaktiviran.
            </span>
          </div>
        </div>

        <!-- Blocked-downgrade explanation (no action available on this card) -->
        <div class="flex w-full mb-4 p-3 bg-grayNavi rounded" *ngIf="!isActive && isDowngrade">
          <div class="text-sm text-left text-grayDark">
            <span *ngIf="conflictingModule" i18n>
              Ta paket je vključen v "{{ conflictingModule.name }}", ki ga že imate aktiviranega.
              Če želite preklopiti na ta paket, najprej prekličite "{{ conflictingModule.name }}".
            </span>
          </div>
        </div>

        <!-- Included Package Features. CarPeak modules render the
             description with the trailing "Več na CarPeak" phrase
             stripped so the feature row doesn't repeat the link copy
             from the top blurb. Other modules render verbatim. -->
        <div class="w-full h-full bg-grayNavi rounded p-3">
          <div class="flex items-start text-sm">
            <i class="fas fa-check text-green mr-2 mt-1 flex-shrink-0"></i>
            <span>{{ isCarPeakModule ? featureDescription : modulePackage.description }}</span>
          </div>
        </div>

        <!-- Upsell / block notice: consolidatable module needs an active paid
             subscription. Shown when the account has none, is expired, or is on
             a trial. -->
        <div class="flex w-full mb-4 p-3 bg-orange-50 border border-orange rounded"
             *ngIf="!isActive && requiresPaidSubscription">
          <i class="text-orange fas fa-lock mr-2 mt-1"></i>
          <div class="text-sm text-left">
            <span *ngIf="isOnTrial" i18n>
              Ta modul lahko aktivirate po prehodu s preizkusne dobe na plačljivo naročnino.
            </span>
            <span *ngIf="!isOnTrial" i18n>
              Za aktivacijo tega modula potrebujete aktivno naročnino.
            </span>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-between mt-3 w-full">
          <div>
            <!-- Activate Button (hidden entirely when blocked by a downgrade - see the explanation box above instead) -->
            <button *ngIf="!isActive && !isDowngrade"
                    (click)="activateModule()"
                    class="btn btn-primary px-3 py-1"
                    [disabled]="isLoading || isDowngrade || requiresPaidSubscription">
              <span *ngIf="!isUpgrade" i18n>Aktiviraj modul</span>
              <span *ngIf="isUpgrade" i18n>Nadgradi modul</span>
            </button>

            <!-- Deactivate Button -->
            <button *ngIf="isActive"
                    (click)="deactivateModule()"
                    class="btn btn-danger-link px-3 py-1"
                    [disabled]="isLoading">
              <span i18n>Deaktiviraj modul</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</ng-template>
